import Table from '@tiptap/extension-table';
import { TableView } from './tableView';
import { columnResizing, isInTable, tableEditing } from '@tiptap/pm/tables';
import { CommandProps, Editor } from '@tiptap/core';
import { NodeSelection } from '@tiptap/pm/state';

const getTableNode = (editor: Editor) => {
  const { selection } = editor.state;

  let tableNode = null;

  if (selection instanceof NodeSelection && selection.node.type.name === 'table') {
    tableNode = selection.$head.node();
  } else {
    const $head = selection.$head;
    for (let d = $head.depth; d > 0; d--) {
      if ($head.node(d).type.name === 'table') {
        tableNode = $head.node(d);
        break;
      }
    }
  }

  return tableNode;
};

const hasFullWidthTable = (editor: Editor) => {
  let hasFullWidth = false;
  editor.state.doc.descendants((node) => {
    if (node.type.name === 'table' && node.attrs['contentWidth'] === 'full') {
      hasFullWidth = true;
      return false;
    }
  });
  return hasFullWidth;
};

export default Table.extend({
  addOptions() {
    return {
      ...this.parent?.(),
      resizable: true,
      allowTableNodeSelection: true,
      View: TableView as any,
      pageSize: false,
    };
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      contentWidth: {
        default: 'page',
      },
    };
  },
  // Making this a custom node just so that typescript works with it
  // breaks the baked-in table functionality (since the node name changes).
  // Normally, there should be some getter we could modify.
  // @ts-ignore
  addCommands() {
    return {
      ...this.parent?.(),
      toggleTableContentWidth:
        () =>
        ({ editor, state, dispatch, commands }: CommandProps) => {
          if (!isInTable(state)) return false;
          if (dispatch) {
            const tableNode = getTableNode(editor);
            if (!tableNode) return false;
            const currentWidth = tableNode.attrs?.contentWidth;
            const newWidth = currentWidth === 'page' ? 'full' : 'page';
            commands.updateAttributes('table', {
              contentWidth: newWidth,
            });

            if (['card', 'page'].includes((editor as any).resourceType)) {
              requestAnimationFrame(() => {
                if (newWidth === 'full') {
                  editor.view.dom.setAttribute('data-table-full', 'true');
                } else if (!hasFullWidthTable(editor)) {
                  editor.view.dom.removeAttribute('data-table-full');
                }
              });
            }
          }
          return true;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        if (this.editor.commands.sinkListItem('listItem')) {
          return true;
        }

        if (this.editor.commands.goToNextCell()) {
          return true;
        }

        if (!this.editor.can().addRowAfter()) {
          return false;
        }

        return this.editor.chain().addRowAfter().goToNextCell().run();
      },
      'Shift-Tab': () => {
        if (this.editor.commands.liftListItem('listItem')) {
          return true;
        }
        return this.editor.commands.goToPreviousCell();
      },
    };
  },

  /**
   * Reference: https://github.com/ueberdosis/tiptap/issues/4044
   */
  addProseMirrorPlugins() {
    const isResizable = this.options.resizable;
    return [
      ...(isResizable
        ? [
            columnResizing({
              handleWidth: this.editor.isEditable ? this.options.handleWidth : 0,
              cellMinWidth: this.options.cellMinWidth,
              // @ts-ignore (incorrect type)
              View: this.options.View,
              // TODO: PR for @types/prosemirror-tables
              // @ts-ignore (incorrect type)
              lastColumnResizable: this.options.lastColumnResizable,
            }),
          ]
        : []),
      tableEditing({
        allowTableNodeSelection: this.options.allowTableNodeSelection,
      }),
    ];
  },
});
